import { create } from '@bufbuild/protobuf';
import { ConnectError } from '@connectrpc/connect';
import useCloudService from '@grpc/composables/useCloudService';
import Heartbeat from '@pinia/features/heartbeat';
import useNodeStore from '@pinia/node';
import { useProjectStore } from '@pinia/project';
import { useSessionStore } from '@pinia/session';
import { SubscribeNotificationsRequestSchema } from 'gpcore-grpc/gpcore/api/cloud/v1/requests_pb';
import { computed, ref } from 'vue';

export default () => {
  const projectStore = useProjectStore();
  const sessionStore = useSessionStore();
  const nodeStore = useNodeStore();

  let abortController = null;
  const isSubscribing = ref(false);
  const timeout = ref(1000);

  const hb = ref(new Heartbeat());

  const subscribeProjects = async () => {
    hb.value.start();

    if (isSubscribing.value) {
      if (abortController != null) abortController.abort();
      console.debug('[Subscriber]: Closed serverLogsSubscription');
      isSubscribing.value = false;
    }

    isSubscribing.value = true;
    abortController = new AbortController();

    const currentProject = computed(() => projectStore.project);

    try {
      for await (const res of useCloudService().subscribeApiCall(
        'subscribeNotifications',
        create(SubscribeNotificationsRequestSchema),
        new Headers(),
        abortController.signal,
      )) {
        console.debug('[Subscriber]: Received message from notifications', res);
        const { notification } = res;
        switch (notification?.message.case) {
          case 'heartbeat':
            hb.value.updateHeartbeat(notification.message.value.seconds);
            break;
          case 'user':
            sessionStore.setMainUser(notification.message.value);
            break;
          case 'project':
            projectStore.setProject(notification.message.value);
            break;
          case 'node':
            if (notification.message.value.projectId === currentProject.value.id) {
              nodeStore.setNode(notification.message.value);
            }
            break;
          default:
            console.debug('[Subscriber]: Unknown message received from notifications', res);
        }
      }
    } catch (error) {
      isSubscribing.value = false;
      if (error instanceof ConnectError && abortController.signal.aborted) {
        // this was an intentional abort
        return;
      }

      abortController.abort();
      // reconnect in 10 seconds
      window.setTimeout(() => {
        subscribeProjects();
        timeout.value += 1000;
        if (timeout.value > 60000) {
          timeout.value = 60000;
        }
      }, timeout.value);

      console.error('[Subscriber]: Error in subscribeProjects', error);
    }

    console.log('[Subscriber]: Exiting subscribeProjects');
  };

  hb.value.onTimeout = () => {
    console.debug('[Subscriber]: Heartbeat timeout, reconnecting');
    subscribeProjects();
  };

  const disconnect = () => {
    if (isSubscribing.value) {
      abortController.abort();
      console.debug('[Subscriber]: Closed serverLogsSubscription');
      isSubscribing.value = false;
    }
  };

  return {
    isSubscribing,
    subscribeProjects,
    disconnect,
  };
};
